import { FlowStepOutputInstructions, FlowStepOutputInstructionType } from "../Structure/FlowStepOutputInstructions";
import { FlowStepSequence } from "../Structure/FlowStepSequence";
import { FlowUtilities } from "../Utilities/FlowUtilities";
import { BaseFlowStepRunner } from "./BaseFlowStepRunner";
import { FlowStepRunnerRegistration } from "../Utilities/RegisterFlowStepRunner";
import { IConfigStorage } from "../../Common/IConfigStorage";
import { DataUtilities } from "../../Common/DataUtilities";
import { AppGlueError } from "../../AppGlueError";
import { StateManager } from "../../Common/StateManager";
import { MultiExecutableOutputDefinition } from "../../Common/Execution/MultiExecutable";
import { ValidationIssue } from "../../Common/IDesignValidationProvider";

export abstract class FlowStepData implements IConfigStorage {
    static IS_SHOW_DESCPTION = "shouldShowDescription";
    static NAME = "name";
    abstract readonly __type: string;
    _id: string = DataUtilities.generateUniqueId();
    name?: string;
    description: string;
    shouldShowDescription: boolean = false;
    // not stored
    runner?: BaseFlowStepRunner;

    onDataChange?: () => void;

    // this is where we put instructions on what to do with non default paths
    protected _nonDefaultOutputInstructions: {
        [path: string]: FlowStepOutputInstructions;
    } = {};

    constructor() {
        this.name = Reflect.get(this, "__type");

        this.description = "";
    }

    private _sequence?: FlowStepSequence;

    get sequence(): FlowStepSequence | undefined {
        return this._sequence;
    }

    set sequence(sequence: FlowStepSequence | undefined) {
        this._sequence = sequence;
        if (this.onDataChange) this.onDataChange();
    }

    syncInputDataDefinition() {
        if (this.onDataChange) this.onDataChange();
    }

    getRunner() {
        if (this.runner) return this.runner;

        let reg = FlowStepRunnerRegistration.getByTypeName(this.__type!);

        if (!reg) throw new AppGlueError("no runner found for " + this.__type!);

        this.runner = reg.create(this);

        return this.runner;
    }

    getOutcomeInstructions(): FlowStepOutputInstructions[] {
        let inst: FlowStepOutputInstructions[] = [];

        let paths = this.getOutcomes();
        if (paths) {
            for (let p of paths) {
                let i: FlowStepOutputInstructions | null = this._nonDefaultOutputInstructions[p.name] ?? null;

                if (!i) {
                    i = new FlowStepOutputInstructions(p.name, this._id);

                    StateManager.addObserver(
                        i,
                        () => {
                            if (this.sequence) StateManager.propertyChanged(this.sequence, FlowStepSequence.STRATEGY);
                        },
                        [FlowStepOutputInstructions.STRATEGY],
                        "flowListener",
                    );

                    this._nonDefaultOutputInstructions[p.name] = i;
                }

                inst.push(i);
            }
        }

        this.validateInstructions(inst);

        return inst;
    }

    getStorageData(): object {
        let data = {};

        Reflect.set(data, "_id", this._id);
        Reflect.set(data, "name", this.name);
        Reflect.set(data, "__type", this.__type);
        Reflect.set(data, "description", this.description);
        Reflect.set(data, "isShowDescription", this.shouldShowDescription);

        let iData: object[] = [];
        if (this._nonDefaultOutputInstructions) {
            for (let i of Object.values(this._nonDefaultOutputInstructions)) {
                iData.push(i.getStorageData());
            }
        }
        Reflect.set(data, "_nonDefaultOutputInstructions", iData);

        return data;
    }

    setStorageData(data: object) {
        let id = Reflect.get(data, "_id");
        this._id = id || DataUtilities.generateUniqueId();

        this.name = Reflect.get(data, "name");
        this.description = Reflect.get(data, "description");
        this.shouldShowDescription = Reflect.get(data, "isShowDescription");

        let iData: [] = Reflect.get(data, "_nonDefaultOutputInstructions") || [];

        for (let d of iData) {
            let i = new FlowStepOutputInstructions("", "");
            i.setStorageData(d);
            this._nonDefaultOutputInstructions[i.pathName] = i;
        }
    }

    findOutputInstruction(name: string): FlowStepOutputInstructions | undefined {
        // todo: optimize this

        for (let i of this.getOutcomeInstructions()) {
            if (i.pathName === name) return i;
        }
    }

    isCurrentOutcome(name: string): boolean {
        let outcomes = this.getOutcomes();

        if (!outcomes) return false;

        for (let o of outcomes) {
            if (o.name === name) return true;
        }

        return false;
    }

    getOutcomes(): MultiExecutableOutputDefinition[] {
        // this is correct.  the base flow step does NOT have to provide paths.
        return [];
    }

    getValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        // if (this.executable) {
        //     let getDesignValidationIssues = Reflect.get(this.executable, "getDesignValidationIssues");

        //     if (typeof getDesignValidationIssues === "function") {
        //         issues.push(
        //             ...getDesignValidationIssues().map((issue: ValidationIssue) => {
        //                 issue.issue = `step [${this.name}] : ${issue.issue}`;
        //                 return issue;
        //             })
        //         );
        //     }
        // }

        for (let idx in this.getOutcomeInstructions()) {
            if (idx === "0") continue;

            let inst = this.getOutcomeInstructions()[idx];
            if (inst.strategy === FlowStepOutputInstructionType.BRANCH) {
                if (!inst.connectedSequenceId) {
                    issues.push(
                        new ValidationIssue(`step [${this.name}]'s path [${inst.pathName}]: not connected sequence`),
                    );
                } else if (this.sequence && this.sequence.flow) {
                    let connectedSeq = this.sequence.flow.allSequences.find((s) => s._id === inst.connectedSequenceId);
                    if (!connectedSeq) {
                        issues.push(
                            new ValidationIssue(
                                `step [${this.name}]'s path [${inst.pathName}]: connected sequence is not correct`,
                            ),
                        );
                    }
                }
            }
        }

        return issues;
    }

    private validateInstructions(inst: FlowStepOutputInstructions[]) {
        if (FlowUtilities.IsEndStep(this)) return;

        if (inst && inst.length !== 0) {
            if (inst[0].strategy !== FlowStepOutputInstructionType.CONTINUE)
                inst[0].strategy = FlowStepOutputInstructionType.CONTINUE;
        }
    }
}
